import { useState } from "react";
import { formatMoney } from "@/lib/format";
import { Pencil, Check, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Breakdown of VAT per tax rate. Read-only by default; pass `editable` + `onChange`
// to allow editing the rates inline (toggled via the pencil icon in the header).
// Renders nothing in read-only mode for fewer than 2 rates.
export function VatBreakdown({ lines, currency, editable = false, onChange }) {
  const [editing, setEditing] = useState(false);
  const rows = lines || [];
  if (!editable && rows.length < 2) return null;

  const netSum = rows.reduce((s, l) => s + Number(l.net || 0), 0);
  const vatSum = rows.reduce((s, l) => s + Number(l.vat || 0), 0);

  const update = (idx, key, val) =>
    onChange?.(rows.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  const addRow = () => onChange?.([...rows, { rate: "", net: "", vat: "" }]);
  const removeRow = (idx) => onChange?.(rows.filter((_, i) => i !== idx));

  return (
    <div
      data-testid="vat-breakdown"
      className="border border-border rounded-sm overflow-hidden"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-muted">
        <span className="data-label">Steuersätze (aufgeschlüsselt)</span>
        {editable && (
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="text-muted-foreground hover:text-primary transition-colors"
            title={editing ? "Fertig" : "Bearbeiten"}
            data-testid="vat-breakdown-edit-toggle"
          >
            {editing ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
          </button>
        )}
      </div>

      {editing ? (
        <div className="divide-y divide-border">
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-3 py-2 bg-muted/40">
            <div className="data-label">Satz %</div>
            <div className="data-label text-right">Netto</div>
            <div className="data-label text-right">MwSt-Betrag</div>
            <div className="w-8" />
          </div>
          {rows.map((l, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-3 py-2 items-center"
              data-testid="vat-breakdown-edit-row"
            >
              <Input
                type="number"
                step="0.1"
                value={l.rate ?? ""}
                onChange={(e) => update(i, "rate", e.target.value)}
                className="bg-white tabular h-9"
                data-testid={`vat-rate-input-${i}`}
              />
              <Input
                type="number"
                step="0.01"
                value={l.net ?? ""}
                onChange={(e) => update(i, "net", e.target.value)}
                className="bg-white tabular h-9 text-right"
                data-testid={`vat-net-input-${i}`}
              />
              <Input
                type="number"
                step="0.01"
                value={l.vat ?? ""}
                onChange={(e) => update(i, "vat", e.target.value)}
                className="bg-white tabular h-9 text-right"
                data-testid={`vat-vat-input-${i}`}
              />
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-muted-foreground hover:text-destructive transition-colors p-1"
                title="Zeile entfernen"
                data-testid={`vat-remove-row-${i}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <div className="px-3 py-2">
            <Button
              type="button"
              variant="secondary"
              onClick={addRow}
              className="border border-border h-8 gap-2"
              data-testid="vat-add-row"
            >
              <Plus className="h-4 w-4" /> Zeile hinzufügen
            </Button>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-border">
          <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-muted/40">
            <div className="data-label">Satz</div>
            <div className="data-label text-right">Netto</div>
            <div className="data-label text-right">MwSt-Betrag</div>
          </div>
          {rows.map((l, i) => (
            <div
              key={i}
              className="grid grid-cols-3 gap-2 px-3 py-2 text-sm tabular"
              data-testid="vat-breakdown-row"
            >
              <div>{Number(l.rate)} %</div>
              <div className="text-right">{formatMoney(l.net, currency)}</div>
              <div className="text-right">{formatMoney(l.vat, currency)}</div>
            </div>
          ))}
          <div className="grid grid-cols-3 gap-2 px-3 py-2 text-sm font-semibold tabular bg-muted/40">
            <div>Summe</div>
            <div className="text-right">{formatMoney(netSum, currency)}</div>
            <div className="text-right">{formatMoney(vatSum, currency)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
